import { ComponentType, Fragment, useCallback, useEffect, useMemo } from 'react'
import {
	CasesApi,
	IDatahubCase,
	OrderStatus,
	TasksApi,
	OrdersApi,
	CaseCategory,
	IGetCasesListParams,
	ICaseTextGroup,
	TextsApi,
	IGetCasesListResponse,
	CaseMaterialStatus,
	ICommonResponse,
	IUpdateCaseAddressParams,
	IAddress,
	FeedbackDrawingType,
	ISimplifiedVisualMaterial,
	FeedbackDrawingShape,
	transformCanvasDocumentToValid,
	getS3TaskUploadTempPath,
	S3Api,
	IDatahubOrderLine,
	IFeedbackLasso,
} from 'api'
import { useLocalization } from 'localization'
import { useShowModal } from 'modules/common/GlobalModals'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Container } from 'components'
import appRoutes from 'app.routes'
import {
	ArrowsHorizontal,
	Compare,
	Document,
	DocumentDownload,
	DocumentView,
	Events,
	Help,
	Image as CarbonImage,
	Map,
	Person,
	Store,
	Chart_3D,
	OverflowMenuVertical,
} from '@carbon/icons-react'

import QUERIES from 'modules/common/queries'
import { useVisualMaterials } from 'modules/material/hooks'
import { useParsedFilters } from 'modules/common/hooks'
import { useScreenMatch, ANT_DROPDOWN_BUTTON_ROUND_CLASS_NAME } from 'theme'
import { ISelectedProduct } from 'modules/common/types'
import { mapFullAddressFromUpdateCaseAddress } from 'api/cases/helpers'
import { ARROW_SIZE, LASSO_BORDER_WIDTH, LASSO_FILL_STYLE, POINT_SIZE } from './FeedbackCanvas/constants'
import { rgbaColor } from 'theme/mixins'
import { useTheme } from 'styled-components'
import dayjs from 'dayjs'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

//const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const Help24 = () => <Help size={24} />

export function useCaseFilterOptions() {
	const { t } = useLocalization()
	const parsedQueryString = useParsedFilters<{
		departmentId: string[]
		category?: string[]
		status?: string[]
		query?: string
		fromDate?: string
		toDate?: string
	}>('cases')
	const { data } = useQuery({ queryKey: [QUERIES.caseFilters], queryFn: CasesApi.getFilters })

	// empty qs should be reinitialized with ALL filters selected
	// query is ignored in order to be able to search if this is the only parameter
	const isQSEmpty = useMemo(
		() =>
			!parsedQueryString.category &&
			!parsedQueryString.departmentId &&
			!parsedQueryString.status &&
			!parsedQueryString.fromDate &&
			!parsedQueryString.toDate,
		[parsedQueryString]
	)

	const categoryOptions = useMemo(
		() =>
			data?.categories.map(item => ({
				value: item,
				label: t(`cases.categories.${item}`),
			})) ?? [],
		[data, t]
	)
	const categoryValues = useMemo(() => {
		const qsArray = parsedQueryString.category as CaseCategory[]

		if (qsArray && categoryOptions.length > 0) {
			return categoryOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value)
		} else if (!qsArray) {
			return isQSEmpty ? categoryOptions.map(option => option.value) : []
		}
		return qsArray
	}, [categoryOptions, parsedQueryString, isQSEmpty])

	const statusOptions = useMemo(
		() =>
			data?.statuses.map(item => ({
				value: item,
				label: t(`cases.statuses.${item}`),
			})) ?? [],
		[data, t]
	)
	const statusValues = useMemo(() => {
		const qsArray = parsedQueryString.status as OrderStatus[]

		if (qsArray && statusOptions.length > 0) {
			return statusOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value)
		} else if (!qsArray) {
			return isQSEmpty ? statusOptions.map(option => option.value) : []
		}
		return qsArray
	}, [statusOptions, parsedQueryString, isQSEmpty])

	const departmentOptions = useMemo(
		() =>
			data?.departments.map(item => ({
				value: item.id,
				label: item.name,
			})) ?? [],
		[data]
	)
	const departmentValues = useMemo(() => {
		const qsArray = parsedQueryString.departmentId

		if (qsArray && departmentOptions.length > 0) {
			return departmentOptions.filter(({ value }) => qsArray.includes(value)).map(option => option.value)
		} else if (!qsArray) {
			return isQSEmpty ? departmentOptions.map(option => option.value) : []
		}
		return qsArray
	}, [departmentOptions, parsedQueryString, isQSEmpty])

	return {
		categoryOptions,
		categoryValues,
		statusOptions,
		statusValues,
		departmentOptions,
		departmentValues,
		parsedQueryString,
	}
}

export function useCasePresentation() {
	const { t } = useLocalization()
	const options = useMemo(
		() => [
			{
				filter: 'all',
				label: t('materials.pages.presentation.links.showAll'),
				mobileLabel: t('materials.pages.presentation.links.presentAll'),
			},
			{
				filter: 'active',
				label: t('materials.pages.presentation.links.showActive'),
				mobileLabel: t('materials.pages.presentation.links.presentActive'),
			},
		],
		[t]
	)

	return { options }
}

const CATEGORY_ICON_MAP: Record<CaseCategory, ComponentType> = {
	[CaseCategory.Case]: () => <Document size={24} />,
	[CaseCategory.Group]: () => <Events size={24} />,
	[CaseCategory.IntroOutro]: () => <ArrowsHorizontal size={24} />,
	[CaseCategory.Location]: () => <Map size={24} />,
	[CaseCategory.Portrait]: () => <Person size={24} />,
	[CaseCategory.Shop]: () => <Store size={24} />,
	[CaseCategory.Matterport]: () => <Chart_3D size={24} />,
	[CaseCategory.BeforeAfter]: () => <Compare size={24} />,
}

export function useCases(params: IGetCasesListParams, queryEnabled = true) {
	const enabled = queryEnabled && (!params.query || params.query.length >= 3)

	const {
		data: rawData = {
			pages: [] as IGetCasesListResponse[],
			pageParams: undefined,
		},
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		isFetched,
		isFetching,
		isRefetchError,
		isError
	} = useInfiniteQuery({
		queryKey: [QUERIES.cases, params],
		queryFn: ({ pageParam, signal }) =>
			CasesApi.getDatahubCases({
				...params,
				page: pageParam,
				signal,
			}),
		initialPageParam: 0,
		enabled,
		getNextPageParam: ({ page, pageSize, totalItems }) => {
			return (page + 1) * pageSize < totalItems ? page + 1 : undefined
		},
		placeholderData: keepPreviousData,
	})

	const data = useMemo(
		() => rawData?.pages.reduce((acc, page) => [...acc, ...page.items], [] as IDatahubCase[]) ?? [],
		[rawData]
	)

	return {
		data,
		isLoading,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		isFetched,
		isFetching,
		isRefetchError,
		isError
	}
}


export function useSimplifiedCaseOrders(caseId?: IDatahubCase['id'], enabled = true) {
	return useQuery({
		queryKey: [QUERIES.orderListSimplified, caseId],
		queryFn: async () => (caseId ? await OrdersApi.getCaseOrders(caseId) : undefined),
		enabled: enabled && !!caseId,
	})
}

export function useCaseDetails(caseId: IDatahubCase['id'], enabled: boolean) {
	const { data: address } = useQuery({
		queryKey: [QUERIES.caseAddress, caseId],
		queryFn: () => CasesApi.getAddressByCaseId(caseId),
		enabled: enabled && !!caseId,
		staleTime: 0, // disable cache for instant updates
	})

	const { data: caseOpenTasks = [] } = useQuery({
		queryKey: [QUERIES.totalTasksByReference, caseId],
		queryFn: () => TasksApi.getOpenTasksByCaseId(caseId),
		enabled: enabled && !!caseId,
	})

	return {
		address,
		totalTasks: caseOpenTasks.length || 0,
	}
}


export const useTranslatedTexts = (caseId?: IDatahubCase['id']) => {
	const { t } = useLocalization()

	const {
		data: groups = [],
		isLoading: areGroupsLoading,
		isFetched: areGroupsFetched,
	} = useQuery({ queryKey: [QUERIES.textGroups], queryFn: TextsApi.getTextGroups })

	const {
		data: caseTexts,
		isLoading: areTextsLoading,
		isFetched: areTextsFetched,
	} = useQuery({
		queryKey: [QUERIES.caseTexts, caseId],
		queryFn: async () => (caseId ? TextsApi.getCaseTexts(caseId) : undefined),
		enabled: !!caseId,
	})

	const translatedTexts = useMemo(
		() =>
			groups.map<ICaseTextGroup>(group => ({
				groupName: t(`cases.textGroups.${group.groupName}`),
				texts: group.texts.map(text => {
					const foundGroup = caseTexts?.find(item => item.groupName === group.groupName)
					if (!foundGroup) return text
					const foundTextContent = foundGroup.texts.find(item => item.name === text.name)

					return {
						...text,
						content: foundTextContent?.content || '',
					}
				}),
			})),
		[caseTexts, groups, t]
	)

	return {
		translatedTexts,
		isLoading: areGroupsLoading || areTextsLoading,
		isFetched: areGroupsFetched && areTextsFetched,
	}
}

export const useProductStorage = () => {
	const STORAGE_KEY = 'case.selected.product'

	const setSelectedProduct = (product: ISelectedProduct) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(product))
	}

	const getSelectedProduct = (): ISelectedProduct | null => {
		try {
			return JSON.parse(localStorage.getItem(STORAGE_KEY) as string)
		} catch (err) {
			return null
		}
	}

	const clearSelectedProduct = () => {
		localStorage.removeItem(STORAGE_KEY)
	}

	return {
		setSelectedProduct,
		getSelectedProduct,
		clearSelectedProduct,
	}
}

export const useCaseById = (caseId?: IDatahubCase['id'], enabled = true) =>
	useQuery({
		queryKey: [QUERIES.caseDetails, caseId],
		queryFn: async ({signal}) => (caseId ? await CasesApi.getDatahubCase(caseId, {
			signal
		}) : undefined),
		enabled: !!caseId && enabled,
	})

export const useContactPersonsByCaseId = (caseId?: IDatahubCase['id'], enabled = true) =>
	useQuery({
		queryKey: [QUERIES.contactPersonsByCaseId, caseId],
		queryFn: async () => (caseId ? await CasesApi.getContactPersonsByCaseId(caseId) : undefined),
		enabled: !!caseId && enabled,
	})

export const useTryUploadResultImageIntoS3 = ({
	drawingComments = [],
	branchId,
	orderlineId,
}: {
	drawingComments: string[]
	branchId?: string
	orderlineId?: IDatahubOrderLine['id']
}) => {
	const theme = useTheme()

	const CANVAS_SETTINGS = useMemo(
		() => ({
			pointDrawingSize: POINT_SIZE,
			arrowDrawingSize: ARROW_SIZE,
			drawingColor: rgbaColor('primary', 0.8)({ theme }),
			drawingFiller: rgbaColor('primary', 0.2)({ theme }),
			labelColor: theme.colors.error['500'],
		}),
		[theme]
	)

	const drawArrow = (context: CanvasRenderingContext2D, p1: { x: number; y: number }, p2: { x: number; y: number }) => {
		const headlen = 10 // length of head in pixels
		const dx = p2.x - p1.x
		const dy = p2.y - p1.y
		const angle = Math.atan2(dy, dx)
		context.moveTo(p1.x, p1.y)
		context.lineTo(p2.x, p2.y)
		context.lineTo(p2.x - headlen * Math.cos(angle - Math.PI / 6), p2.y - headlen * Math.sin(angle - Math.PI / 6))
		context.moveTo(p2.x, p2.y)
		context.lineTo(p2.x - headlen * Math.cos(angle + Math.PI / 6), p2.y - headlen * Math.sin(angle + Math.PI / 6))
	}

	const getImageFromCanvas = (
		drawings: FeedbackDrawingType[],
		material: ISimplifiedVisualMaterial
	): Promise<string | undefined | null> =>
		new Promise((resolve, reject) => {
			const lassoDrawings = drawings.filter(drawing => drawing.shape === FeedbackDrawingShape.Lasso)
			const pointDrawings = drawings.filter(drawing => drawing.shape === FeedbackDrawingShape.Point)
			const arrowDrawings = drawings.filter(drawing => drawing.shape === FeedbackDrawingShape.Arrow)

			if (drawings.length === 0) return resolve(null)

			const img = new Image()
			img.crossOrigin = 'anonymous'
			img.onload = () => {
				const canvas = document.createElement('canvas')
				canvas.width = img.width
				canvas.height = img.height

				const context = canvas.getContext('2d') as CanvasRenderingContext2D

				context.drawImage(img, 0, 0, img.width, img.height, 0, 0, img.width, img.height)

				for (const drawing of lassoDrawings) {
					const { points } = drawing as IFeedbackLasso
					const { labelPoint, color, comment } = transformCanvasDocumentToValid(
						{ ...CANVAS_SETTINGS, size: [img.width, img.height] },
						drawing,
						drawingComments.length
					)

					context.fillStyle = LASSO_FILL_STYLE
					context.strokeStyle = color
					context.lineWidth = LASSO_BORDER_WIDTH
					context.beginPath()

					context.moveTo(points[0][0], points[0][1])

					for (let i = 1; i < points.length; i++) {
						context.lineTo(points[i][0], points[i][1])
					}

					context.closePath()
					context.stroke()
					context.fill()

					context.font = 'bold 24px sans-serif'
					context.fillStyle = CANVAS_SETTINGS.labelColor
					drawingComments.push(comment)
					context.fillText(drawingComments.length.toString(), labelPoint.x, labelPoint.y)
				}

				for (const drawing of pointDrawings) {
					const { labelPoint, comment, color, p1, p2 } = transformCanvasDocumentToValid(
						{ ...CANVAS_SETTINGS, size: [img.width, img.height] },
						drawing,
						drawingComments.length
					)

					context.fillStyle = CANVAS_SETTINGS.drawingFiller
					context.strokeStyle = color
					const oldAlpha = context.globalAlpha
					context.globalAlpha = 0
					context.rect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y)
					context.fill()
					context.globalAlpha = oldAlpha
					context.stroke()

					context.font = 'bold 24px sans-serif'
					context.fillStyle = CANVAS_SETTINGS.labelColor
					drawingComments.push(comment)
					context.fillText(drawingComments.length.toString(), labelPoint.x - 8, labelPoint.y + 8)
				}

				for (const drawing of arrowDrawings) {
					const { labelPoint, comment, p1, p2 } = transformCanvasDocumentToValid(
						{ ...CANVAS_SETTINGS, size: [img.width, img.height] },
						drawing,
						drawingComments.length
					)

					context.strokeStyle = CANVAS_SETTINGS.drawingColor
					context.lineWidth = LASSO_BORDER_WIDTH

					drawArrow(context, p1, p2)

					context.stroke()

					context.font = 'bold 24px sans-serif'
					context.fillStyle = CANVAS_SETTINGS.labelColor
					drawingComments.push(comment)
					context.fillText(drawingComments.length.toString(), labelPoint.x, labelPoint.y)
				}

				resolve(canvas.toDataURL())
			}
			img.onerror = () => reject()
			img.src = `${material.source}?t=${Date.now()}`
		})

	const uploadImage = async (file: Blob | null, material: ISimplifiedVisualMaterial) => {
		if (!file) return ''

		const s3Key = getS3TaskUploadTempPath({
			task: { branchId: branchId || 'feedback', orderlineId },
			fileUuid: material.id,
			fileExtension: 'png',
		})

		const s3re = await S3Api.signS3Upload({
			expiration: dayjs().add(2, 'minute').toISOString(),
			filePath: s3Key,
		})

		const { preSignedUrl } = s3re

		await S3Api.s3Upload({
			preSignedUrl,
			file: file,
		})

		return preSignedUrl.split('?')[0]
	}

	const tryUploadResultImageIntoS3 = async (material: ISimplifiedVisualMaterial, drawings: FeedbackDrawingType[]) => {
		const imageFromCanvas = await getImageFromCanvas(drawings, material)
		let uploadedDrawingsUrl: string | null = null

		if (imageFromCanvas) {
			const resp = await fetch(imageFromCanvas as string)
			const imgBlob = await resp.blob()
			uploadedDrawingsUrl = await uploadImage(imgBlob, material)
		}

		return uploadedDrawingsUrl || material.source
	}

	return tryUploadResultImageIntoS3
}
